import { apiClient } from './client';

/**
 * Client for `/api/admin/capabilities` (Phase 14). Stays in lockstep with
 * `server/src/routes/admin/capabilities.routes.ts` and the Zod enum in
 * `server/src/capabilities/types.ts`.
 *
 * The capability vocabulary lives here too so the UI can list options
 * without round-tripping the server. Adding a capability is a code
 * change in BOTH files — the server's Zod enum is the source of truth
 * for what the API accepts, this list is the source of truth for what
 * the UI offers.
 */

export const CAPABILITY_OPTIONS = [
  {
    value: 'manage_alerts',
    label: 'Manage alerts',
    description: 'Configure alert subscriptions for self + others. Required to use the Alerts settings.',
  },
  {
    value: 'view_alert_dispatches',
    label: 'View alert dispatches',
    description: 'Read recent dispatches + the email bodies that were sent.',
  },
  {
    value: 'upload_time_data',
    label: 'Upload time data',
    description: 'Upload Gusto CSV (or JSON) reports to populate the overtime tracker.',
  },
  {
    value: 'manage_eos',
    label: 'Manage EOS',
    description: 'Edit the V/TO, manage company/department rocks, administer People Analyzer rounds.',
  },
  {
    value: 'run_meetings',
    label: 'Run meetings',
    description: 'Create/edit L10 meeting series, facilitate meetings, rank issues.',
  },
  {
    value: 'manage_sop_revisions',
    label: 'Manage SOP revision chain',
    description:
      'Edit the per-org approval chain that SOP revision requests walk (who reviews, in what order).',
  },
  {
    value: 'manage_training',
    label: 'Manage training',
    description: 'Author courses and quizzes, manage assignments and onboarding checklist templates.',
  },
  {
    value: 'view_training_reports',
    label: 'View training reports',
    description: 'Completion matrix, overdue lists, per-hire checklist progress, SOP acknowledgment lists.',
  },
  {
    value: 'use_cs',
    label: 'Use customer service',
    description: 'The CS agent workspace — ticket queue, replies, assignment, macros.',
  },
  {
    value: 'manage_cs_routing',
    label: 'Manage CS routing',
    description: 'Routing rules, groups and fallbacks, SLA policies, tag taxonomy, macro admin.',
  },
  {
    value: 'view_cs_operations',
    label: 'View CS operations',
    description: 'Operations tab reads — health checks, integration errors, ops agent, reports.',
  },
  {
    value: 'manage_cs_agents',
    label: 'Manage CS agents',
    description: 'Create, suspend, and remove CS agent profiles.',
  },
  {
    value: 'manage_cs_credentials',
    label: 'Manage CS credentials',
    description: 'Credential rotation tracking and the CS security policy.',
  },
  {
    value: 'manage_announcements',
    label: 'Manage announcements',
    description: 'Publish, edit, and archive announcements; see who has read and acknowledged them.',
  },
  {
    value: 'manage_documents',
    label: 'Manage documents',
    description:
      'Upload, edit, and archive documents and set who may access them (roles, seats, departments, or people), including the AI access suggestion.',
  },
  {
    value: 'use_clients',
    label: 'Use client CRM',
    description:
      'The onboarding specialist workspace: client pipeline, records, onboardings, task review, and saved views.',
  },
  {
    value: 'manage_client_config',
    label: 'Manage client config',
    description:
      'Configure client types, onboarding templates, and module settings; delete clients and share saved views.',
  },
  {
    value: 'manage_investigations',
    label: 'Manage investigations',
    description:
      'Configure investigation rules, issue types, causes and stations; act on any record; void; decide intake candidates.',
  },
  {
    value: 'view_investigation_attribution',
    label: 'View investigation attribution',
    description:
      'See which person was associated with a nonconformance (quality and leads only). Every reveal is audited.',
  },
] as const;

export type Capability = (typeof CAPABILITY_OPTIONS)[number]['value'];

export interface MembershipCapability {
  id: string;
  membershipId: string;
  capability: Capability;
  grantedAt: string;
  grantedByMembershipId: string | null;
}

export async function listCapabilities(membershipId?: string): Promise<MembershipCapability[]> {
  const query = membershipId ? `?membershipId=${encodeURIComponent(membershipId)}` : '';
  const res = await apiClient.get<{ data: MembershipCapability[] }>(`/api/admin/capabilities${query}`);
  return res.data.data;
}

export interface GrantCapabilityRequest {
  membershipId: string;
  capability: Capability;
}

export async function grantCapability(body: GrantCapabilityRequest): Promise<MembershipCapability> {
  const res = await apiClient.post<{ data: MembershipCapability }>('/api/admin/capabilities', body);
  return res.data.data;
}

export async function revokeCapability(id: string): Promise<void> {
  await apiClient.delete(`/api/admin/capabilities/${encodeURIComponent(id)}`);
}
